import { createSelector } from 'reselect';

// Selectors for any PricingData object
const selectDiscountedTotal = pricing => ({
  net: pricing.netDiscountedCents,
  gross: pricing.grossDiscountedCents,
});
const selectListTotal = pricing => ({
  net: pricing.netListCents,
  gross: pricing.grossListCents,
});
const selectCurrency = pricing => pricing.currency;
const selectUiLocale = state => {
  if (state && state.uiLocalization && state.uiLocalization.locale) {
    return state.uiLocalization.locale;
  }
  return 'en-IE';
};
const selectFractionDigits = pricing => {
  const fallbackFractionDigits = pricing.currency === 'JPY' ? 0 : 2;
  return pricing.fractionDigits === undefined || pricing.fractionDigits === null
    ? fallbackFractionDigits
    : pricing.fractionDigits; // In most cases this is 2, except JPY; used to divide throughout.
};

const selectPricingComponents = pricing => pricing.components || [];

const selectProductTotal = pricing => {
  if (pricing.components) {
    const productPriceComponent = pricing.components.find(
      comp => comp.type === 'PRODUCT_TOTAL'
    );
    if (productPriceComponent) {
      return {
        netDiscounted: productPriceComponent.netDiscountedCents,
        grossDiscounted: productPriceComponent.grossDiscountedCents,
        netList: productPriceComponent.netListCents,
        grossList: productPriceComponent.grossListCents,
      };
    }
  }
};

const selectPricingIsValid = pricing => pricing.pricingIsValid;
const selectTax = pricing => pricing.taxTotalCents;
const selectTaxBreakdown = pricing => pricing.taxes || [];
const selectTaxTypes = pricing => pricing.taxTypes || [];

// Coupon selectors
const selectCentsDiscountedFromList = pricing =>
  pricing.grossListCents - pricing.grossDiscountedCents;
const selectDiscountCodeName = pricing =>
  pricing.discountSource?.type?.toLowerCase() === 'coupon'
    ? pricing.discountSource?.id
    : undefined;

const selectCouponChangeInProgress = pricing => pricing.couponChangeInProgress;
const selectCouponErrorMessageKey = pricing => pricing.couponErrorMessageKey;
const selectCouponErrorSubstitutions = pricing =>
  pricing.couponErrorSubstitutions;
const selectVatId = pricing => pricing.vatId;

// Selectors for PricingData on a CartItem
const selectPricingDataByItemId = (pricing, itemId) =>
  pricing.itemPricing[itemId] || {};
const selectIsPricingValidForItemId = (pricing, itemId) =>
  pricing.itemPricing[itemId] && pricing.itemPricing[itemId].pricingValid;
const selectPricingInvalidityReasonForItemId = (pricing, itemId) =>
  pricing.itemPricing[itemId] &&
  pricing.itemPricing[itemId].pricingInvalidityReason;

const selectPricingStatusKeyForItemId = createSelector(
  [pricing => pricing, (pricing, itemId) => itemId],
  (pricing, itemId) =>
    pricing.itemPricing[itemId] && pricing.itemPricing[itemId].pricingStatusKey
);

const selectPricingInvalidityReasonsFromAllItems = pricing =>
  Object.keys(pricing.itemPricing)
    .filter(itemId => pricing.itemPricing[itemId])
    .map(
      itemId =>
        `${itemId}: ${pricing.itemPricing[itemId].pricingInvalidityReason}`
    );

const selectItemIdsWithCartTemplatePricing = pricing =>
  Object.keys(pricing.templatePricing);

const selectCartTemplatePricingByItemId = (pricing, itemId) =>
  pricing.templatePricing[itemId];

const selectTemplateBillingDisplayTypes = createSelector(
  pricing => pricing,
  pricing => {
    const types = {};

    for (const { templateBillingDisplayType } of Object.values(
      pricing.templatePricing || {}
    )) {
      if (!types[templateBillingDisplayType])
        types[templateBillingDisplayType] = templateBillingDisplayType;
    }

    return Object.keys(types);
  }
);

const selectPricingSummariesForBillingDisplayType = (pricing, templateType) =>
  Object.values(pricing.templatePricing || {}).filter(
    templatePricingSummary =>
      templatePricingSummary.templateBillingDisplayType === templateType
  );

// Selectors for PricingData of itemGroup
const selectItemGroupPricingDataByGroupId = (pricing, groupId) =>
  pricing.itemGroupPricing[groupId] || {};

const selectDiscountSource = pricing => pricing.discountSource;

const selectDiscountSources = pricing => pricing.discountSources;

// shipping

const selectContainsDeliverableProducts = pricing =>
  !!pricing.shippingPricing?.containsDeliverableProducts;

const selectShippingTotalCents = pricing =>
  pricing.shippingPricing?.pricingData
    ? {
        netDiscounted: pricing.shippingPricing.pricingData.netDiscountedCents,
        grossDiscounted:
          pricing.shippingPricing.pricingData.grossDiscountedCents,
        netList: pricing.shippingPricing.pricingData.netListCents,
        grossList: pricing.shippingPricing.pricingData.grossListCents,
      }
    : undefined;

const selectShippingMethodDisplayValue = (pricing, locale) => {
  let localizedShippingDisplayName =
    pricing.shippingPricing?.shippingMethodName;
  if (
    pricing.shippingPricing?.localizedDisplayInfo &&
    pricing.shippingPricing.localizedDisplayInfo[locale]
  ) {
    localizedShippingDisplayName =
      pricing.shippingPricing.localizedDisplayInfo[locale].shippingMethodName;
  }

  return localizedShippingDisplayName;
};

const selectItemPricing = pricing => pricing.itemPricing || {};

const selectTemplatePricingSummaries = pricing => pricing.templatePricing || {};

const selectShippingPricing = pricing => pricing.shippingPricing || {};

const selectItemIdsOfTemplatePricingSummariesForDisplayType = createSelector(
  [pricing => pricing, (pricing, templateType) => templateType],
  (pricing, templateType) => {
    const itemIds = Object.keys(pricing.templatePricing || {});
    return itemIds.filter(id => {
      const summary = pricing.templatePricing[id] || {};
      if (summary.templateBillingDisplayType === templateType) {
        return id;
      }
      return undefined;
    });
  }
);

const selectTemplateDiscountSourcesByDisplayType = createSelector(
  [pricing => pricing, (pricing, templateType) => templateType],
  (pricing, templateType) =>
    selectItemIdsOfTemplatePricingSummariesForDisplayType(pricing, templateType)
      .map(id => pricing.templatePricing[id]?.discountSource)
      .filter(source => source !== undefined)
);

const selectTemplateDiscountSourceByDisplayType = (pricing, templateType) =>
  // arbitrarily use the first one; we don't know which one is the "best" discount source
  selectTemplateDiscountSourcesByDisplayType(pricing, templateType)[0];

const selectItemPricingIssueKeys = createSelector(
  [pricing => pricing, (pricing, cartItemIds) => cartItemIds],
  (pricing, cartItemIds) => {
    const itemStatusKeys = cartItemIds
      .map(id => selectPricingStatusKeyForItemId(pricing, id))
      .map((v, i) => (i === 0 ? `${v}` : `,  ${v}`));

    return itemStatusKeys;
  }
);

const selectIsNegativeDiscountApplied = pricing => {
  const isCouponCodeApplied = !!selectDiscountCodeName(pricing);
  const hasPreviouslyAppliedDiscountInfo =
    !!pricing.previouslyAppliedDiscountInfo?.grossDiscountedCents;
  return (
    isCouponCodeApplied &&
    hasPreviouslyAppliedDiscountInfo &&
    pricing.grossDiscountedCents >
      pricing.previouslyAppliedDiscountInfo.grossDiscountedCents
  );
};

const selectPreviouslyAppliedDiscountCodeName = pricing =>
  pricing.previouslyAppliedDiscountInfo?.couponCode;

export default {
  selectTemplateDiscountSourcesByDisplayType,
  selectTemplateDiscountSourceByDisplayType,
  selectDiscountedTotal,
  selectListTotal,
  selectTax,
  selectPricingIsValid,
  selectCurrency,
  selectUiLocale,
  selectFractionDigits,
  selectPricingDataByItemId,
  selectPricingStatusKeyForItemId,
  selectPricingComponents,
  selectCentsDiscountedFromList,
  selectTaxBreakdown,
  selectVatId,
  selectDiscountCodeName,
  selectCouponChangeInProgress,
  selectCouponErrorMessageKey,
  selectCouponErrorSubstitutions,
  selectIsPricingValidForItemId,
  selectPricingInvalidityReasonForItemId,
  selectPricingInvalidityReasonsFromAllItems,
  selectProductTotal,
  selectItemIdsWithCartTemplatePricing,
  selectCartTemplatePricingByItemId,
  selectTemplateBillingDisplayTypes,
  selectPricingSummariesForBillingDisplayType,
  selectItemGroupPricingDataByGroupId,
  selectTaxTypes,
  selectDiscountSource,
  selectContainsDeliverableProducts,
  selectShippingTotalCents,
  selectShippingMethodDisplayValue,
  selectItemPricing,
  selectTemplatePricingSummaries,
  selectShippingPricing,
  selectItemIdsOfTemplatePricingSummariesForDisplayType,
  selectDiscountSources,
  selectItemPricingIssueKeys,
  selectIsNegativeDiscountApplied,
  selectPreviouslyAppliedDiscountCodeName,
};
